import TranslationUtils from '@/i18n/TranslationUtils';

import Utils from '@/api/Utils';


type TableColumn = {
	key?: string;
	dataIndex?: string;
	title?: string;
	align?: 'left' | 'right' | 'center';
	width?: string | number;
	minWidth?: number;
	responsive?: ReadonlyArray<'xs' | 'sm' | 'md' | 'lg' | 'xl' | 'xxl' | 'xxxl'>;
	sorter?: boolean | ((a: unknown, b: unknown) => number) | { compare: (a: unknown, b: unknown) => number };
};


const COLORS = {
	dark0: '#001529',
	primary0: '#1890ff',

	success: '#52c41a',
	default: '#d9d9d9',
	processing: '#1890ff',
	error: '#ff4d4f',
	warning: '#faad14',

	successDark: '#429d15',
	processingDark: '#1373cc',
	errorDark: '#d66565',
	warningDark: '#b6870f',

	successDarker: '#338000',

	successLight: '#aff08e',
	errorLight: '#ffb3b3',
	warningLight: '#fffbe6',

	successUltraLight: '#cff6bb',
	errorUltraLight: '#ffe5e6',

	successUltraDark: '#194d00',
	processingUltraDark: '#0c4a9a',
	errorUltraDark: '#800000',

	nearServiceColor: '#001529',
	vrsServiceColor: '#8113cc',
	settingsSpecialModeGradientOrange: '#fa8c16',
	settingsSpecialModeGradientPurple: '#722ed1',
	settingsSpecialModeGradientOrangeSelected: '#ffc53d',
	settingsSpecialModeGradientPurpleSelected: '#9254de',

	featureStrokeColor: '#ffffff',

	pink: '#f02090',

	gray: '#999999',
	grayDark: '#666666',

	brown: '#8B4513',
} as const;

const HEAT_MAP_COLORS = {
	blue: '3588b4',
	green: '44b61a',
	yellow: 'd6b03d',
	red: 'e32624',
} as const;

const OPACITY = {
	full: 'ff',
	half: '99',
	low: '44',
} as const;

const CSS = {
	paddingMd: '16px',
} as const;

const PROJECT_TITLE_DEFAULT = 'CORS';


const BASE_STATIONS_TABLE_COLUMNS = [
	{ key: 'status' },
	{ key: 'statusOnline' },
	{ key: 'statusMerged' },
	{ key: 'mountpoints' },
	{
		dataIndex: 'label',
		key: 'label',
	},
	{ key: 'antDetails' },
	{
		dataIndex: 'id',
		key: 'actions',
	},
	{
		dataIndex: 'displayAddress',
		key: 'displayAddress',
		responsive: ['xl', 'xxl', 'xxxl'],
	},
] satisfies TableColumn[];


const BASE_STATIONS_IMPORT_TABLE_COLUMNS = [
	{ dataIndex: 'mntpntInt' },
	{ dataIndex: 'label' },
	{ dataIndex: 'status' },
	{ dataIndex: 'message' },
] satisfies TableColumn[];

const baseStationsImportTableColumnsComp = TranslationUtils.addTranslatedField(
	BASE_STATIONS_IMPORT_TABLE_COLUMNS,
	'dataIndex', 'title',
	'confirmImportModal.tableColumns',
);

const CNET_IMPORT_CONFIRM_TABLE_COLUMNS = [
	{ dataIndex: 'cn_num' },
	{ dataIndex: 'gridpoint_count' },
	{ dataIndex: 'week' },
	{ dataIndex: 'status' },
	{ dataIndex: 'message' },
] satisfies TableColumn[];

const cnetImportConfirmTableColumnsComp = TranslationUtils.addTranslatedField(
	CNET_IMPORT_CONFIRM_TABLE_COLUMNS,
	'dataIndex', 'title',
	'settings.vrsSettingsForm.pppRtk.cnetImportConfirmModal.tableColumns',
);

const USER_MANAGEMENT_TABLE_COLUMNS = [
	{ dataIndex: 'username' },
	{ dataIndex: 'tempPassword' },
	{ dataIndex: 'casterPassword' },
	{
		dataIndex: 'role',
		width: '250px',
	},
	{
		dataIndex: 'connectionLimitNearVrs',
		width: '150px',
	},
	{
		dataIndex: 'geoFencingEnabled',
	},
	{
		dataIndex: 'accessibleBaseStations',
	},
	{
		dataIndex: 'vrsEnabled',
		width: '100px',
	},
	{
		dataIndex: 'nearEnabled',
		width: '100px',
	},
	{
		dataIndex: 'cssrEnabled',
		width: '100px',
	},
	{
		dataIndex: 'brdcEnabled',
		width: '100px',
	},
	{
		dataIndex: 'enabled',
		width: '100px',
	},
	{
		dataIndex: 'actions',
		width: '100px',
	},
] satisfies TableColumn[];


const CHART_COLORS = [
	'e99115', '3fc19f', 'bc425a', '199ee5', 'e21f1c',
	'691fdf', 'd8cf26', '3dc50b', '0a2df4', '633426',
	'fd6d5d', '000000', '688096', '888888', 'bbbbbb',
	'F4C88A', '8CCEF2', 'eb0f90', '38761d',
] as const;


const CHART_LIGHT_COLORS = [
	'f2bd73', '8bdac5', 'd78e9c', '75c5f0', 'ee7977',
	'b48fef', 'f4f1be', '93f76e', '8596fa', 'cd907e',
	'febbb3', '404040', 'b4c0cb', 'bfbfbf', 'f2f2f2',
	'f8deb9', 'e8f5fc', 'fab7df', '71d247',
] as const;


type GnssDataItem = {
	code: string;
	color: string;
	colorLight: string;
	name: string;
	showInLegend: boolean;
	isActual: boolean;
};

const GNSS_DATA = [
	{ code: 'G', color: CHART_COLORS[0], colorLight: CHART_LIGHT_COLORS[0], name: 'GPS', showInLegend: true, isActual: true },
	{ code: 'R', color: CHART_COLORS[3], colorLight: CHART_LIGHT_COLORS[3], name: 'GLO', showInLegend: true, isActual: true },
	{ code: 'E', color: CHART_COLORS[7], colorLight: CHART_LIGHT_COLORS[7], name: 'GAL', showInLegend: true, isActual: true },
	{ code: 'J', color: CHART_COLORS[2], colorLight: CHART_LIGHT_COLORS[2], name: 'QZS', showInLegend: false, isActual: false },
	{ code: 'C', color: CHART_COLORS[6], colorLight: CHART_LIGHT_COLORS[6], name: 'BDS', showInLegend: false, isActual: true },
	{ code: 'S', color: CHART_COLORS[4], colorLight: CHART_LIGHT_COLORS[4], name: 'SBS', showInLegend: false, isActual: false },
] as const satisfies readonly GnssDataItem[];

type GnssCode = typeof GNSS_DATA[number]['code'];
type ActualGnssCode = Extract<typeof GNSS_DATA[number], { readonly isActual: true }>['code'];

type GnssBadge = {
	code: ActualGnssCode;
	forceGrey: boolean;
};

const GNSS_DATA_CODE_ORDER: readonly GnssCode[] = GNSS_DATA.map(item => item.code);

const BRDCM_PUBLISH_INTERVAL_SEC_CHOICES = [5, 10, 15, 30, 60] as const;
const BRDCM_MAX_TOE_MINUTES_CHOICES = [30, 60, 90, 120, 150, 180, 210, 240] as const;

const sortGnssCodes = (codes: readonly string[]): string[] => (
	[...codes].sort(Utils.sortArrayAccordingToValuesComparator<string>(GNSS_DATA_CODE_ORDER))
);


const getColor = (index: number, colors: readonly string[]): string => {
	if (colors.length === 0) {
		throw new Error('getColor: colors array must not be empty');
	}
	const color = colors[index % colors.length];
	if (color === undefined) {
		throw new Error('getColor: missing color at index ' + (index % colors.length));
	}
	return '#' + color;
};


const AXIS_STYLE = {
	fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, 'Noto Sans', sans-serif, 'Apple Color Emoji', 'Segoe UI Emoji', 'Segoe UI Symbol', 'Noto Color Emoji'",
	fontWeight: 'normal',
	fontSize: '13px',
} as const;


const MAIN_STATS_TABLE_COLUMNS = [
	{ dataIndex: 'labelStr' },
	{
		dataIndex: 'inTraffic',
		align: 'right',
	},
	{
		dataIndex: 'outTraffic',
		align: 'right',
	},
	{
		dataIndex: 'connects',
		align: 'right',
	},
	{
		dataIndex: 'lastUpdate',
		align: 'right',
	},
] satisfies TableColumn[];

const mainStatsTableColumnsComp = TranslationUtils.addTranslatedField(
	MAIN_STATS_TABLE_COLUMNS,
	'dataIndex', 'title',
	'stats.network-metrics.tableColumns',
);


const ROVER_STATUSES_TABLE_COLUMNS = [
	{ dataIndex: 'client' },
	{ dataIndex: 'service' },
	{
		dataIndex: 'active',
		align: 'right',
	},
	{
		dataIndex: 'pending',
		align: 'right',
	},
	{
		dataIndex: 'failed',
		align: 'right',
	},
] satisfies TableColumn[];

const roverStatusesColumnsComp = TranslationUtils.addTranslatedField(
	ROVER_STATUSES_TABLE_COLUMNS,
	'dataIndex', 'title',
	'rovers.roverStatuses.tableColumns',
);


const ROVERS_TOO_FAR_TABLE_COLUMNS = [
	{ dataIndex: 'client' },
	{ dataIndex: 'service' },
	{
		dataIndex: 'connects',
		align: 'right',
	},
] satisfies TableColumn[];

const roversTooFarColumnsComp = TranslationUtils.addTranslatedField(
	ROVERS_TOO_FAR_TABLE_COLUMNS,
	'dataIndex', 'title',
	'rovers.roverStatuses.tableColumns',
);


const ACTIVE_ROVER_SESSIONS_TABLE_COLUMNS = [
	{ dataIndex: 'session_id' },
	{ dataIndex: 'username' },
	{ dataIndex: 'ip_address' },
	{ dataIndex: 'service' },
	{ dataIndex: 'quality' },
	{ dataIndex: 'updated' },
] satisfies TableColumn[];

const activeRoverSessionsTableColumnsComp = TranslationUtils.addTranslatedField(
	ACTIVE_ROVER_SESSIONS_TABLE_COLUMNS,
	'dataIndex', 'title',
	'rovers.activeSessions.tableColumns',
);


const EVENTS_TABLE_COLUMNS = [
	{ dataIndex: 'source' },
	{
		dataIndex: 'count',
		align: 'right',
	},
	{
		dataIndex: 'first_event',
		align: 'right',
	},
	{
		dataIndex: 'last_event',
		align: 'right',
	},
	{
		dataIndex: 'trend',
		align: 'right',
	},
] satisfies TableColumn[];

const eventsTableColumnsComp = TranslationUtils.addTranslatedField(
	EVENTS_TABLE_COLUMNS,
	'dataIndex', 'title',
	'events.tableColumns',
);


const GETTER_FAILED_TOOLTIP_TABLE_COLUMNS = [
	{ dataIndex: 'username' },
	{ dataIndex: 'attempts' },
] satisfies TableColumn[];

const getterFailedTooltipTableColumnsComp = TranslationUtils.addTranslatedField(
	GETTER_FAILED_TOOLTIP_TABLE_COLUMNS,
	'dataIndex', 'title',
	'home-map.tooltip.tableColumns',
);


const PAAS_REVIEW_TABLE_COLUMNS = [
	{ dataIndex: 'mntpntInt' },
	{ dataIndex: 'label' },
	{ dataIndex: 'status' },
	{ dataIndex: 'antArp' },
	{ dataIndex: 'antDescription' },
	{ dataIndex: 'distance', align: 'right' },
	{ dataIndex: 'xDiff', align: 'right' },
	{ dataIndex: 'yDiff', align: 'right' },
	{ dataIndex: 'zDiff', align: 'right' },
] satisfies TableColumn[];

const paasReviewTableColumnsComp = TranslationUtils.addTranslatedField(
	PAAS_REVIEW_TABLE_COLUMNS,
	'dataIndex', 'title',
	'paas.reviewModal.tableColumns',
);


const LINKED_TELEGRAMS_TABLE_COLUMNS = [
	{ dataIndex: 'telegramUsername' },
	{ dataIndex: 'alertsEnabled' },
	{ dataIndex: 'errorEnabled' },
	{ dataIndex: 'warningEnabled' },
	{ dataIndex: 'infoEnabled' },
	{ dataIndex: 'debugEnabled' },
	{ dataIndex: 'delete' },
	{ dataIndex: 'test' },
] satisfies TableColumn[];

const linkedTelegramsTableColumnsComp = TranslationUtils.addTranslatedField(
	LINKED_TELEGRAMS_TABLE_COLUMNS,
	'dataIndex', 'title',
	'account.linkedTelegrams.tableColumns',
);

const LINKED_EMAILS_TABLE_COLUMNS = [
	{ dataIndex: 'email' },
	{ dataIndex: 'alertsEnabled' },
	{ dataIndex: 'alertLevel' },
	{ dataIndex: 'deliveryPeriod' },
	{ dataIndex: 'delete' },
	{ dataIndex: 'test' },
] satisfies TableColumn[];

const linkedEmailsTableColumnsComp = TranslationUtils.addTranslatedField(
	LINKED_EMAILS_TABLE_COLUMNS,
	'dataIndex', 'title',
	'account.linkedEmails.tableColumns',
);

const EXTERNAL_API_KEYS_TABLE_COLUMNS = [
	{ dataIndex: 'name' },
	{ dataIndex: 'status' },
	{
		dataIndex: 'isEnabled',
		width: '100px',
	},
	{ dataIndex: 'prefix' },
	{ dataIndex: 'permissions' },
	{ dataIndex: 'lastUsedAt' },
	{ dataIndex: 'createdAt' },
	{ dataIndex: 'revokedAt' },
	{ dataIndex: 'actions' },
] satisfies TableColumn[];

const externalApiKeysTableColumnsComp = TranslationUtils.addTranslatedField(
	EXTERNAL_API_KEYS_TABLE_COLUMNS,
	'dataIndex', 'title',
	'account.externalApiKeys.tableColumns',
);


const GNSS_VISIBLE_SIGNALS_TABLE_COLUMNS = [
	{ dataIndex: 'gnssName' },
	{ dataIndex: 'signals' },
	{ dataIndex: 'currentDatetimeBright' },
] satisfies TableColumn[];

const gnssVisibleSignalsTableColumnsComp = TranslationUtils.addTranslatedField(
	GNSS_VISIBLE_SIGNALS_TABLE_COLUMNS,
	'dataIndex', 'title',
	'gnss-visibility.tableColumns',
);


const BASELINES_TABLE_COLUMNS = [
	{ key: 'id', sorter: Utils.objectFieldMultiSorter('id', 'number') },
	{ key: 'statusOnline', sorter: Utils.objectFieldMultiSorter('statusData.key') },
	{ key: 'base', sorter: Utils.objectFieldMultiSorter('base.mntpnt_int') },
	{ key: 'rover', sorter: Utils.objectFieldMultiSorter('rover.mntpnt_int') },
	{ key: 'nmea_port_ext', dataIndex: 'nmea_port_ext', sorter: Utils.objectFieldMultiSorter('nmea_port_ext', 'number') },
	{ key: 'selected_gnss' },
	{ key: 'actions' },
] satisfies TableColumn[];

const baselinesTableColumnsComp = TranslationUtils.addTranslatedField(
	BASELINES_TABLE_COLUMNS,
	'key', 'title',
	'baselines.tableColumns',
);


const LAST_GGA_TABLE_COLUMNS = [
	{ dataIndex: 'epoch' },
	{ dataIndex: 'n_coord', align: 'right' },
	{ dataIndex: 'e_coord', align: 'right' },
	{ dataIndex: 'u_coord', align: 'right' },
	{ dataIndex: 'quality', align: 'right' },
	{ dataIndex: 'satellites_count', align: 'right' },
] satisfies TableColumn[];

const lastGGATableColumnsComp = TranslationUtils.addTranslatedField(
	LAST_GGA_TABLE_COLUMNS,
	'dataIndex', 'title',
	'baselines-edit.lastGGA.tableColumns',
);

// сколько дней отображаем на всех графиках страницы БС
const BASE_STATION_PAGE_DAYS_COUNT = -14;

const FTP_NAVIGATOR_COLUMNS = [
	{
		dataIndex: 'name',
	},
	{
		dataIndex: 'size',
		sorter: Utils.objectFieldMultiSorter('size', 'number'),
	},
	{
		dataIndex: 'modified',
		sorter: Utils.objectFieldMultiSorter('modified'),
	},
] satisfies TableColumn[];

const ftpNavigatorColumnsComp = TranslationUtils.addTranslatedField(
	FTP_NAVIGATOR_COLUMNS,
	'dataIndex', 'title',
	'settings.ftp-navigator.tableColumns',
);


const CNET_LIST_TABLE_COLUMNS = [
	{ dataIndex: 'id' },
	{ dataIndex: 'title' },
	{ dataIndex: 'num_gp' },
	{ dataIndex: 'created' },
	{ dataIndex: 'updated' },
	{ dataIndex: 'actions' },
	{ dataIndex: 'used_in_subnetworks' },
] satisfies TableColumn[];

const cnetListTableColumnsComp = TranslationUtils.addTranslatedField(
	CNET_LIST_TABLE_COLUMNS,
	'dataIndex', 'title',
	'cnet-constructor-list.tableColumns',
);

const RTCM_MESSAGES_TABLE_COLUMNS = [
	{ dataIndex: 'index' },
	{ dataIndex: 'label' },
] satisfies TableColumn[];

const rtcmMessagesTableColumnsComp = TranslationUtils.addTranslatedField(
	RTCM_MESSAGES_TABLE_COLUMNS,
	'dataIndex', 'title',
	'net-info-table.rtcm-messages.tableColumns',
);


const RTCM_MESSAGES_FILTERS_TABLE_COLUMNS = [
	{ dataIndex: 'code' },
	{ dataIndex: 'label' },
	{ dataIndex: 'period' },
] satisfies TableColumn[];

const rtcmMessagesFiltersTableColumnsComp = TranslationUtils.addTranslatedField(
	RTCM_MESSAGES_FILTERS_TABLE_COLUMNS,
	'dataIndex', 'title',
	'settings.vrsSettingsForm.rtcmMessagesFilters.tableColumns',
);

const cssrMessagesFiltersTableColumnsComp = TranslationUtils.addTranslatedField(
	RTCM_MESSAGES_FILTERS_TABLE_COLUMNS,
	'dataIndex', 'title',
	'settings.vrsSettingsForm.cssrMessagesFilters.tableColumns',
);

const BRDCM_STATIONS_TABLE_COLUMNS = [
	{ dataIndex: 'station', width: 240 },
	{ dataIndex: 'ephemerides', width: 200 },
] satisfies TableColumn[];

const BRDCM_RTCM_EPHEMERIS_BY_GNSS = {
	G: 1019,
	R: 1020,
	E: 1046,
	C: 1042,
} as const satisfies Record<ActualGnssCode, number>;

const BRDCM_STATION_GNSS_TABLE_COLUMNS = GNSS_DATA
	.filter(item => item.isActual)
	.map(item => ({
		dataIndex: item.code,
		width: 56,
		align: 'center' as const,
	}));

const BRDCM_STATIONS_TABLE_SCROLL_X = [
	...BRDCM_STATIONS_TABLE_COLUMNS,
	...BRDCM_STATION_GNSS_TABLE_COLUMNS,
].reduce((sum, column) => sum + Number(column.width ?? 0), 0);

const brdcmStationsStationColumnComp = TranslationUtils.addTranslatedField(
	BRDCM_STATIONS_TABLE_COLUMNS,
	'dataIndex', 'title',
	'settings.vrsSettingsForm.brdcm.tableColumns',
);

const LEAP_SECONDS_TABLE_COLUMNS = [
	{ dataIndex: 'date' },
	{ dataIndex: 'value' },
	{ dataIndex: 'actions', key: 'actions' },
] satisfies TableColumn[];

const leapSecondsTableColumnsComp = TranslationUtils.addTranslatedField(
	LEAP_SECONDS_TABLE_COLUMNS,
	'dataIndex', 'title',
	'settings.vrsSettingsForm.lsFile.tableColumns',
);

const LEAP_SECONDS_MONTH_DAY_VALUES = ['01-01', '07-01'] as const;
const LEAP_SECONDS_MONTH_DAY_OPTIONS = LEAP_SECONDS_MONTH_DAY_VALUES.map(value => ({
	value,
	label: value,
}));

const LEAP_SECONDS_FIRMWARE_PREVIEW_COUNT = 3;


const APPLY_CNET_MODAL_TABLE_COLUMNS = [
	{ dataIndex: 'subgrid' },
	{ dataIndex: 'cnet_number' },
	{ dataIndex: 'cnet_date' },
] satisfies TableColumn[];

const applyCnetModalTableColumnsComp = TranslationUtils.addTranslatedField(
	APPLY_CNET_MODAL_TABLE_COLUMNS,
	'dataIndex', 'title',
	'cnet-constructor.legend.saveModal.tableColumns',
);


const RECOMMENDED_COORDS_TABLE_COLUMNS = [
	{ dataIndex: 'x_coord', align: 'right' },
	{ dataIndex: 'y_coord', align: 'right' },
	{ dataIndex: 'z_coord', align: 'right' },
	{ dataIndex: 'source' },
	{ dataIndex: 'actions', key: 'actions' },
] satisfies TableColumn[];

const recommendedCoordsTableColumnsComp = TranslationUtils.addTranslatedField(
	RECOMMENDED_COORDS_TABLE_COLUMNS,
	'dataIndex', 'title',
	'base-stations-edit.fields.recommendedCoords.tableColumns',
);


const CNET_OTHER_GRIDS_TABLE_COLUMNS = [
	{ dataIndex: 'title' },
	{ dataIndex: 'num_gp', align: 'right', width: '100px' },
] satisfies TableColumn[];

const cnetOtherGridsTableColumnsComp = TranslationUtils.addTranslatedField(
	CNET_OTHER_GRIDS_TABLE_COLUMNS,
	'dataIndex', 'title',
	'cnet-constructor.legend.otherGrids.tableColumns',
);


const STATS_AGGREGATION_TABLE_COLUMNS = [
	{ dataIndex: 'module_label' },
	{ dataIndex: 'kind' },
	{ dataIndex: 'period' },
	{ dataIndex: 'first_datetime' },
	{ dataIndex: 'last_datetime' },
	{ dataIndex: 'last_aggregated_datetime' },
	{ dataIndex: 'percent' },
] satisfies TableColumn[];

const statsAggregationTableColumnsComp = TranslationUtils.addTranslatedField(
	STATS_AGGREGATION_TABLE_COLUMNS,
	'dataIndex', 'title',
	'settings.statsAggregation.tableColumns',
);

const PERFORMANCE_TABLE_COLUMNS = [
	{ dataIndex: 'command_name', sorter: { compare: Utils.objectFieldMultiSorter('command_name', 'string') }},
	{ dataIndex: 'last_run' },
	{ dataIndex: 'avg_duration_seconds', align: 'right', sorter: { compare: Utils.objectFieldMultiSorter('avg_duration_seconds', 'number') }},
	{ dataIndex: 'max_duration_seconds', align: 'right', sorter: { compare: Utils.objectFieldMultiSorter('max_duration_seconds', 'number') }},
	{ dataIndex: 'avg_memory_bytes', align: 'right', sorter: { compare: Utils.objectFieldMultiSorter('avg_memory_bytes', 'number') }},
	{ dataIndex: 'max_memory_bytes', align: 'right', sorter: { compare: Utils.objectFieldMultiSorter('max_memory_bytes', 'number') }},
	{ dataIndex: 'avg_cpu_seconds', align: 'right', sorter: { compare: Utils.objectFieldMultiSorter('avg_cpu_seconds', 'number') }},
	{ dataIndex: 'max_cpu_seconds', align: 'right', sorter: { compare: Utils.objectFieldMultiSorter('max_cpu_seconds', 'number') }},
	{ dataIndex: 'avg_thread_count', align: 'right', sorter: { compare: Utils.objectFieldMultiSorter('avg_thread_count', 'number') }},
	{ dataIndex: 'max_thread_count', align: 'right', sorter: { compare: Utils.objectFieldMultiSorter('max_thread_count', 'number') }},
	{ dataIndex: 'avg_open_files_count', align: 'right', sorter: { compare: Utils.objectFieldMultiSorter('avg_open_files_count', 'number') }},
	{ dataIndex: 'max_open_files_count', align: 'right', sorter: { compare: Utils.objectFieldMultiSorter('max_open_files_count', 'number') }},
] satisfies TableColumn[];

const performanceTableColumnsComp = TranslationUtils.addTranslatedField(
	PERFORMANCE_TABLE_COLUMNS,
	'dataIndex', 'title',
	'settings.performance.tableColumns',
);

const VERSION_HISTORY_TABLE_COLUMNS = [
	{ dataIndex: 'webui_version' },
	{ dataIndex: 'run_at' },
] satisfies TableColumn[];

const versionHistoryTableColumnsComp = TranslationUtils.addTranslatedField(
	VERSION_HISTORY_TABLE_COLUMNS,
	'dataIndex', 'title',
	'settings.versionHistory.tableColumns',
);

const PROCESS_MEMORY_TABLE_COLUMNS = [
	{ dataIndex: 'process_label' },
	{ dataIndex: 'pid', align: 'right' },
	{ dataIndex: 'memory_bytes', align: 'right' },
] satisfies TableColumn[];

const processMemoryTableColumnsComp = TranslationUtils.addTranslatedField(
	PROCESS_MEMORY_TABLE_COLUMNS,
	'dataIndex', 'title',
	'settings.processMemory.tableColumns',
);


const DATABASE_INDEXES_TABLE_COLUMNS = [
	{ dataIndex: 'database_alias', sorter: true },
	{ dataIndex: 'table_name', sorter: true },
	{ dataIndex: 'index_name', sorter: true },
	{ dataIndex: 'index_type', sorter: true },
	{ dataIndex: 'sizeLabel', align: 'right' as const, sorter: true },
	{ dataIndex: 'idxScanLabel', align: 'right' as const, sorter: true },
	{ dataIndex: 'leafDensityLabel', align: 'right' },
	{ dataIndex: 'leafFragmentationLabel', align: 'right' },
	{ dataIndex: 'statsCollectedAtLabel' },
	{ dataIndex: 'catalogFlagsLabel' },
	{ dataIndex: 'fastStatus', sorter: true },
	{ dataIndex: 'fastDurationLabel', align: 'right', sorter: true },
	{ dataIndex: 'extendedStatus', sorter: true },
	{ dataIndex: 'extendedDurationLabel', align: 'right', sorter: true },
	{ dataIndex: 'deepStatus', sorter: true },
	{ dataIndex: 'deepDurationLabel', align: 'right', sorter: true },
	{ dataIndex: 'unresolved_integrity_error_at', sorter: true },
	{ dataIndex: 'actions' },
] satisfies TableColumn[];

const databaseIndexesTableColumnsComp = TranslationUtils.addTranslatedField(
	DATABASE_INDEXES_TABLE_COLUMNS,
	'dataIndex', 'title',
	'settings.databaseIndexes.tableColumns',
);


const AGGREGATE_BS_SAT_SET_COUNT = 'bsSatSetCount';
const AGGREGATE_BS_SET_COUNT = 'bsSetCount';

const NEAR_ELEVATION_BANDS = ['1', '2', '5'] as const;

// Spoofing heatmap: bands evaluated per GNSS (dropdown offers L1/L2/L5)
const GNSS_POSSIBLE_BANDS = {
	G: ['1', '2'],
	R: ['1', '2'],
	E: ['1', '5'],
	C: ['1', '5'],
} as const satisfies Record<string, readonly string[]>;

// Used as runtime gate for multi-c-net UI (C-Net select, warnings, chart requests)
const MULTI_CNET_ENABLED = true;

const CSSR_UPDATE_INTERVALS = {
	2: 5,
	3: 10,
	4: 15,
	5: 30,
	6: 60,
	7: 120,
	8: 240,
	9: 300,
	10: 600,
} as const satisfies Record<number, number>;

const SUBNETWORK_MEASUREMENTS_TIME_INTERVALS = [1, 5, 10] as const;


const Consts = {
	BASE_STATIONS_TABLE_COLUMNS,
	baseStationsImportTableColumnsComp,
	cnetImportConfirmTableColumnsComp,
	USER_MANAGEMENT_TABLE_COLUMNS,
	mainStatsTableColumnsComp,
	roverStatusesColumnsComp,
	roversTooFarColumnsComp,
	activeRoverSessionsTableColumnsComp,
	eventsTableColumnsComp,
	getterFailedTooltipTableColumnsComp,
	paasReviewTableColumnsComp,
	linkedTelegramsTableColumnsComp,
	linkedEmailsTableColumnsComp,
	externalApiKeysTableColumnsComp,
	gnssVisibleSignalsTableColumnsComp,
	baselinesTableColumnsComp,
	lastGGATableColumnsComp,
	ftpNavigatorColumnsComp,
	cnetListTableColumnsComp,
	rtcmMessagesTableColumnsComp,
	rtcmMessagesFiltersTableColumnsComp,
	cssrMessagesFiltersTableColumnsComp,
	brdcmStationsStationColumnComp,
	BRDCM_STATION_GNSS_TABLE_COLUMNS,
	BRDCM_STATIONS_TABLE_SCROLL_X,
	BRDCM_RTCM_EPHEMERIS_BY_GNSS,
	BRDCM_PUBLISH_INTERVAL_SEC_CHOICES,
	BRDCM_MAX_TOE_MINUTES_CHOICES,
	leapSecondsTableColumnsComp,
	leapSecondsMonthDayOptions: LEAP_SECONDS_MONTH_DAY_OPTIONS,
	leapSecondsFirmwarePreviewCount: LEAP_SECONDS_FIRMWARE_PREVIEW_COUNT,
	applyCnetModalTableColumnsComp,
	recommendedCoordsTableColumnsComp,
	cnetOtherGridsTableColumnsComp,
	statsAggregationTableColumnsComp,
	performanceTableColumnsComp,
	versionHistoryTableColumnsComp,
	processMemoryTableColumnsComp,
	databaseIndexesTableColumnsComp,
	COLORS,
	HEAT_MAP_COLORS,
	OPACITY,
	CSS,
	PROJECT_TITLE_DEFAULT,
	CHART_COLORS,
	CHART_LIGHT_COLORS,
	AXIS_STYLE,
	BASE_STATION_PAGE_DAYS_COUNT,
	getColor,
	CSSR_UPDATE_INTERVALS,
	SUBNETWORK_MEASUREMENTS_TIME_INTERVALS,
	AGGREGATE_BS_SAT_SET_COUNT,
	AGGREGATE_BS_SET_COUNT,
	NEAR_ELEVATION_BANDS,
	GNSS_POSSIBLE_BANDS,
	MULTI_CNET_ENABLED,
	GNSS_DATA,
	GNSS_DATA_CODE_ORDER,
	sortGnssCodes,
} as const;

// TODO(TS_MIGRATION) -- use later: GnssCode
export type { GnssDataItem, ActualGnssCode, GnssBadge };

export default Consts;
